import React, { useEffect, useMemo, useState } from 'react'
import {
  FocusContext,
  FocusHandler,
  useFocusable,
} from '@noriginmedia/norigin-spatial-navigation'

import tts from '../../lib/TTS'
import { platformTransitionStyle } from '../../../../shared/helpers'
import { isArrowLeft, isArrowRight } from '.'

import type { VirtualizedListProps } from './types'

const DEFAULT_HIDDEN_ITEMS = 2

export const VirtualizedList = <T,>({
  data: originalData,
  hasMoreContent,
  layout,
  hiddenItems = DEFAULT_HIDDEN_ITEMS,
  ttsText,
  children,
  onFocus,
  handleInfiniteFetching,
}: VirtualizedListProps<T>) => {
  const data = hasMoreContent
    ? [...originalData.slice(0, 12), { item_type: 'seeAll' }]
    : originalData
  const dataLength = data.length
  const {
    ref: scrollingRef,
    focusKey,
    hasFocusedChild,
  } = useFocusable({ trackChildren: true, onFocus })
  const itemWidth = layout.cardWidth + layout.cardMarginRight
  const containerWidth = itemWidth * layout.screenVisibleItems
  const visibleItems = Math.ceil(containerWidth / itemWidth)
  let maxVisibleItems = visibleItems + hiddenItems
  const [startIndex, setStartIndex] = useState(0)
  const [readCarouselTitle, setReadCarouselTitle] = useState(false)
  const [translate, setTranslate] = useState(0)
  const [lastIndexFocused, setLastIndexFocused] = useState(-1)
  const endIndex = Math.min(startIndex + maxVisibleItems, dataLength)
  const invisibleItemsWidthEnd = (dataLength - endIndex) * itemWidth
  const invisibleItemsWidthStart = startIndex * itemWidth
  const scrollableContainerWidth = dataLength * itemWidth

  const cards = useMemo(() => data.slice(startIndex, endIndex), [startIndex])

  maxVisibleItems =
    maxVisibleItems - hiddenItems >= dataLength ? dataLength : maxVisibleItems

  useEffect(() => {
    if (hasFocusedChild) {
      setReadCarouselTitle(true)
    } else {
      setReadCarouselTitle(false)
    }
  }, [hasFocusedChild, setReadCarouselTitle])

  useEffect(() => {
    if (readCarouselTitle) {
      tts.pushTextToRead(ttsText || '')
    }
  }, [readCarouselTitle])

  function handleCardFocus(
    ...args: Parameters<FocusHandler<{ index: number }>>
  ) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [_, { index }, details] = args

    if (index !== lastIndexFocused) {
      // @ts-ignore @TODO: spatial-navigation types are not up to date
      if (isArrowRight(details.event)) {
        if (index + 1 >= visibleItems) {
          setTranslate((previous) => previous - itemWidth)
        }

        if (index >= visibleItems) {
          const newStartIndex = Math.min(
            dataLength - maxVisibleItems + 2,
            startIndex + 1
          )
          setStartIndex(newStartIndex)
        }
        if (
          typeof handleInfiniteFetching === 'function' &&
          dataLength - index === 4
        ) {
          handleInfiniteFetching()
        }
      }
      // @ts-ignore @TODO: spatial-navigation types are not up to date
      if (isArrowLeft(details.event)) {
        if (index >= visibleItems - 2) {
          setTranslate((previous) => previous + itemWidth)
        }

        if (index >= visibleItems - 1) {
          const newStartIndex = Math.max(startIndex - 1, 0)
          setStartIndex(newStartIndex)
        }
      }
      setLastIndexFocused(index)
    }
  }

  return (
    <FocusContext.Provider value={focusKey}>
      <div
        className="virtualized-list"
        style={{ marginBottom: `${layout.carouselMarginBottom}px` }}
      >
        <div
          className="virtualized-list__container"
          ref={scrollingRef}
          style={{ width: `${scrollableContainerWidth}px` }}
        >
          <div
            className="virtualized-list__scrollable-container"
            style={{
              width: `${scrollableContainerWidth}px`,
              transform: `translateX(${translate}px)`,
              transition: platformTransitionStyle(
                'transform 500ms ease-in-out'
              ),
            }}
          >
            <div
              className="virtualized-list__invisible-items-start"
              style={{ width: `${invisibleItemsWidthStart}px` }}
            />
            <div
              className="virtualized-list__row-container"
              style={{
                width: `${cards.length * itemWidth + 100}px`,
              }}
            >
              <div className="virtualized-list__row">
                {typeof children === 'function' &&
                  useMemo(
                    () =>
                      children({
                        cards,
                        startIndex,
                        onFocus: handleCardFocus,
                        readCarouselTitle,
                        handleCardFocus,
                      }),
                    [children, readCarouselTitle, startIndex]
                  )}
              </div>
            </div>
            <div
              className="virtualized-list__invisible-items-end"
              style={{ width: `${invisibleItemsWidthEnd}px` }}
            />
          </div>
        </div>
      </div>
    </FocusContext.Provider>
  )
}
